import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import type { FormulatorErrorMessage } from "@/lib/toolbox/formulator";
import { cn } from "@/lib/utils";

export interface FormulatorNoticeProps {
  message: FormulatorErrorMessage;
  /** Extra actions after the message, such as Try again. */
  children?: ReactNode;
  className?: string;
  testId?: string;
}

/**
 * A plain message for something the Formulator could not do (an engine
 * error, a failed turn), with a link to the Setup checks page when that is
 * where the person fixes it (PI-SPEC-SW-1418).
 */
export function FormulatorNotice({ message, children, className, testId = "formulator-notice" }: FormulatorNoticeProps) {
  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2.5 text-[13px]",
        className
      )}
      role="alert"
      data-testid={testId}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="leading-relaxed">{message.text}</span>
        {(message.setup || children) && (
          <div className="flex flex-wrap items-center gap-3">
            {message.setup && (
              <Link to="/toolbox/setup" className="font-medium text-link hover:underline" data-testid="formulator-setup-link">
                Open Setup checks
              </Link>
            )}
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
